// src/api/admin.ts — Admin alert functions + authenticated admin-portal data.

import { ApiResult } from "../types/ApiResult";

// ---------------------------------------------------------------------------
// Admin portal — authenticated data endpoints
//
// SECURITY: these send the customer JWT as `Authorization: Bearer <token>`.
// The serverless functions (admin-*.js) re-verify that token server-side and
// confirm the email is on the admin allowlist (utils/admin-auth.js). Passing a
// non-admin (or absent) token gets a 401 — the client-side gate is only UX.
// ---------------------------------------------------------------------------

const adminAuthHeaders = (authToken: string): HeadersInit => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${authToken}`,
});

// ── Tax blobs (Netlify Blobs: nexus-tracking) ──────────────────────────────

export interface AdminTaxState {
  state: string;
  salesTotalCents: number;
  transactionCount: number;
  alertSent: boolean;
  hasNexus: boolean;
  salesProgress: number;
  txProgress: number;
  nearThreshold: boolean;
}

export interface AdminTaxBlobs {
  blobsAvailable: boolean;
  storeName: string;
  blobKey: string;
  thresholds: {
    salesThresholdCents: number;
    transactionThreshold: number;
    alertFraction: number;
    salesAlertAtCents: number;
    transactionAlertAt: number;
  };
  nexusStates: string[];
  states: AdminTaxState[];
}

export const getAdminTaxBlobs = async (
  authToken: string,
): Promise<ApiResult<AdminTaxBlobs>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-tax-blobs", {
      method: "GET",
      headers: adminAuthHeaders(authToken),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to load tax data: ${error}` };
  }
};

// ── Feed health (Netlify Blobs: feed-health) ───────────────────────────────
//
// What the daily canary (check-feed-health.mjs) last saw when it fetched
// /meta-feed.csv and /sitemap.xml. Read-only: the portal reports the canary's
// result rather than re-probing, so the tab and the alert email can never
// disagree about what happened.

export interface AdminFeedTarget {
  name: string;
  url: string;
  /** Who pulls this endpoint, e.g. "Meta Commerce Manager (scheduled pull)". */
  consumer: string;
  /** What `count` counts — "variants", "URLs". */
  unit: string;
  status: number;
  contentType: string;
  /** True when the edge function answered, not the SPA fallback. */
  servedByEdge: boolean;
  ms: number;
  bytes: number;
  count: number;
  /** Count on the last healthy run, for the collapse check. Null with no baseline. */
  previousCount: number | null;
  healthy: boolean;
  problems: string[];
  floor: number;
}

export interface AdminFeedHistoryPoint {
  checkedAt: string;
  healthy: boolean;
  counts: Record<string, number>;
}

export interface AdminFeedHealth {
  blobsAvailable: boolean;
  storeName: string;
  blobKey: string;
  /** Null until the canary has run once. */
  checkedAt: string | null;
  ageMs: number | null;
  /** True when the last run is missing or older than staleAfterMs — the canary
   *  itself has gone quiet, which is a finding in its own right. */
  stale: boolean;
  staleAfterMs: number;
  healthy: boolean | null;
  base: string | null;
  targets: AdminFeedTarget[];
  history: AdminFeedHistoryPoint[];
}

export const getAdminFeedHealth = async (
  authToken: string,
): Promise<ApiResult<AdminFeedHealth>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-feed-health", {
      method: "GET",
      headers: adminAuthHeaders(authToken),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to load feed health: ${error}` };
  }
};

// ── Sales & margin dashboard ────────────────────────────────────────────────

export interface AdminTopProduct {
  name: string;
  quantity: number;
  revenueCents: number;
  cogsCents: number;
  /** null when the product has any uncosted units (margin can't be trusted). */
  marginCents: number | null;
  marginPct: number | null;
  costed: boolean;
}

export interface AdminSalesTimePoint {
  /** YYYY-MM-DD (UTC). */
  date: string;
  /** Product revenue (post-discount, pre tax/shipping). */
  revenueCents: number;
  taxCents: number;
  shippingCents: number;
  /** revenue + tax + shipping — the full amount collected that day. */
  totalCollectedCents: number;
  orders: number;
  /** Revenue − COGS for the day; null when any counted unit lacked a cost. */
  marginCents: number | null;
}

export interface AdminCostSnapshotMeta {
  available: boolean;
  updatedAt?: string | null;
  environment?: string | null;
  syncProducts?: number;
  syncVariants?: number;
  costedVariants?: number;
  uncostedVariants?: number;
}

export interface AdminSalesSummary {
  environment: "production" | "sandbox";
  days: number;
  generatedAt: string;
  /** True when the order scan hit the page cap (older orders not included). */
  truncated: boolean;
  costSnapshot: AdminCostSnapshotMeta;
  orderCount: number;
  /** Total collected incl. tax + shipping (+ any donation). */
  totalCollectedCents: number;
  /** totalCollected − tax − shipping. */
  productRevenueCents: number;
  taxCents: number;
  shippingCents: number;
  discountCents: number;
  /** Sum of PARTIAL refunds on counted orders (not deducted from revenue). */
  refundedCents: number;
  aovCents: number;
  cogsCents: number;
  marginCents: number;
  marginPct: number | null;
  costedUnits: number;
  uncostedUnits: number;
  topProducts: AdminTopProduct[];
  timeseries: AdminSalesTimePoint[];
}

/**
 * Aggregated sales for the last `days` days (0 = all time). Read-only.
 * `full` returns the COMPLETE per-product breakdown (for the economic report)
 * instead of just the dashboard's top slice.
 */
export const getAdminSalesSummary = async (
  authToken: string,
  days = 30,
  full = false,
): Promise<ApiResult<AdminSalesSummary>> => {
  try {
    const res = await fetch(
      `/.netlify/functions/admin-sales-summary?days=${days}${full ? "&full=1" : ""}`,
      { method: "GET", headers: adminAuthHeaders(authToken) },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to load sales summary: ${error}` };
  }
};

/**
 * Just the cost-snapshot freshness meta — no order scan, no sales window. Used
 * by the Pricing tab, which cares about how fresh the Printful costs are but
 * has no date range of its own.
 */
export const getAdminCostSnapshot = async (
  authToken: string,
): Promise<ApiResult<{ costSnapshot: AdminCostSnapshotMeta }>> => {
  try {
    const res = await fetch(
      "/.netlify/functions/admin-sales-summary?snapshotOnly=1",
      { method: "GET", headers: adminAuthHeaders(authToken) },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to load cost snapshot: ${error}` };
  }
};

/**
 * Rebuild the Printful cost snapshot (slow live pull → Netlify Blobs cache).
 *
 * Rate-limited server-side to one refresh per 10 minutes — a second burst trips
 * Printful and the retry-sleep then overruns the function. A refused refresh
 * comes back with `retryAfterSeconds` set: that's a cooldown, not a failure, and
 * the cached snapshot is untouched.
 */
export const refreshAdminCosts = async (
  authToken: string,
): Promise<
  ApiResult<{ costSnapshot: AdminCostSnapshotMeta }> & {
    retryAfterSeconds?: number;
  }
> => {
  try {
    const res = await fetch("/.netlify/functions/admin-sales-summary", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ refreshCosts: true }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to refresh costs: ${error}` };
  }
};

// ── Catalog pricing report ──────────────────────────────────────────────────

export interface AdminPricingSizeRow {
  /** Size bucket: "STD" (S–XL / one-size), "2XL", "3XL", or "4XL". */
  size: string;
  variations: number;
  /** Lowest current price in the bucket (conservative). */
  priceCents: number;
  /** Highest Printful blank cost in the bucket; null when uncosted. */
  costCents: number | null;
  marginPct: number | null;
  /** Exact price (to the cent) at which this size hits the target margin. */
  minimumCents: number | null;
  recommendedCents: number | null;
  recommendedMarginPct: number | null;
  /** What the size currently charges over the base size. */
  currentSurchargeCents: number;
  /** What the size actually costs Printful over the base size. */
  costStepCents: number;
}

/** One decoration on the product: where it goes and how it's applied. */
export interface AdminPlacement {
  /** Printful placement id, e.g. "embroidery_chest_left", "label_inside". */
  placement: string;
  technique: string | null;
  /** Printful's price for this placement; null when it isn't in the price table. */
  priceCents: number | null;
  /** Paid options selected on it (e.g. unlimited_color → +$3.95). */
  optionCents: number;
  options: string[];
  /** True for the one decoration the blank cost already covers (the dearest). */
  included: boolean;
}

/**
 * Advisory placement rollup. The blank cost covers ONE decoration, so anything
 * beyond the first is real cost the margin numbers don't see. Nothing else in
 * this report is derived from these fields — they're operator context only, and
 * the add-on is approximate (~4% high on the one product with a known all-in).
 */
export interface AdminPricingPlacements {
  /** Total decorations on the product. */
  count: number;
  /** Decorations beyond the first — the ones the blank cost misses. */
  extraCount: number;
  /** Extra Printful cost per unit, in cents. */
  addOnCents: number;
  /** What to add to the recommended PRICE to carry that cost at the target. */
  priceImpactCents: number;
  /** False when a placement had no price on file — the add-on is then a floor. */
  priced: boolean;
  list: AdminPlacement[];
}

export interface AdminPricingProduct {
  name: string;
  variationCount: number;
  uncostedVariations: number;
  baseSize: string;
  costCents: number | null;
  currentCents: number;
  currentMarginPct: number | null;
  /** Lowest margin across sizes (big sizes cost more at the same retail). */
  worstMarginPct: number | null;
  /** Cheapest / priciest single variation's blank cost (every size × color). */
  costLoCents: number | null;
  costHiCents: number | null;
  /** Worst / best single-variation margin at current prices. */
  marginLoPct: number | null;
  marginHiPct: number | null;
  /** Base size's minimum price at the target margin (unrounded floor). */
  minimumCents: number | null;
  /** Minimum for the priciest variation (costHiCents) at the target margin. */
  minimumHiCents: number | null;
  recommendedCents: number | null;
  recommendedMarginPct: number | null;
  /** "raise" (any size below target), "keep" (already healthy), "no-cost". */
  action: "raise" | "keep" | "no-cost";
  /** Advisory decoration rollup; null when no placements are on file. */
  placements: AdminPricingPlacements | null;
  sizes: AdminPricingSizeRow[];
}

export interface AdminPricingReport {
  environment: "production" | "sandbox";
  generatedAt: string;
  costSnapshot: AdminCostSnapshotMeta;
  targetPct: number;
  summary: {
    products: number;
    raise: number;
    keep: number;
    noCost: number;
    /** Products whose blank cost misses a decoration or a paid upgrade. */
    costUnderstated: number;
  };
  /** Sorted worst current margin first; no-cost items last. */
  products: AdminPricingProduct[];
}

/**
 * Catalog-wide pricing report (current price vs Printful cost vs recommended
 * price at the target margin %). Read-only; independent of any date range.
 */
export const getAdminPricingReport = async (
  authToken: string,
  targetPct = 48,
): Promise<ApiResult<AdminPricingReport>> => {
  try {
    const res = await fetch(
      `/.netlify/functions/admin-pricing-report?target=${targetPct}`,
      { method: "GET", headers: adminAuthHeaders(authToken) },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to load pricing report: ${error}` };
  }
};

// ── Printful catalog health ────────────────────────────────────────────────

export type PrintfulHealthStatus =
  | "in_good_standing"
  | "discontinuing"
  | "out_of_stock"
  | "discontinued"
  | "unknown";

export interface AdminPrintfulVariant {
  id: number | null;
  name: string;
  sku: string | null;
  catalogVariantId: number | null;
  externalId: string | null;
  linkedToSquare: boolean;
  retailPrice: string | null;
  status: PrintfulHealthStatus;
}

export interface AdminPrintfulProduct {
  id: number | null;
  name: string;
  thumbnail: string | null;
  variantCount: number;
  linkedVariantCount: number;
  status: PrintfulHealthStatus;
  variants: AdminPrintfulVariant[];
}

export interface AdminPrintfulCatalog {
  summary: {
    totalProducts: number;
    inGoodStanding: number;
    discontinuing: number;
    discontinued: number;
    outOfStock: number;
    unknown: number;
    totalVariants: number;
    unlinkedVariants: number;
  };
  products: AdminPrintfulProduct[];
}

export const getAdminPrintfulCatalog = async (
  authToken: string,
): Promise<ApiResult<AdminPrintfulCatalog>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-printful-catalog", {
      method: "GET",
      headers: adminAuthHeaders(authToken),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to load Printful catalog: ${error}` };
  }
};

// ── Catalog visibility (portal-controlled hide/unhide) ─────────────────────

export interface AdminCatalogItem {
  id: string;
  name: string;
  hidden: boolean;
}

export interface AdminCatalogVisibility {
  items: AdminCatalogItem[];
  hiddenCount: number;
}

export const getCatalogVisibility = async (
  authToken: string,
): Promise<ApiResult<AdminCatalogVisibility>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-catalog-visibility", {
      method: "GET",
      headers: adminAuthHeaders(authToken),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to load catalog visibility: ${error}` };
  }
};

export const setItemHidden = async (
  authToken: string,
  itemId: string,
  hidden: boolean,
): Promise<ApiResult<{ hiddenIds: string[]; hiddenCount: number }>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-catalog-visibility", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ itemId, hidden }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to update visibility: ${error}` };
  }
};

// ── SKU sync ────────────────────────────────────────────────────────────────

export interface AdminSkuChange {
  itemName: string;
  variationName: string;
  oldSku: string;
  newSku: string;
}

export interface AdminSkuUnmatched {
  itemName: string;
  variationName: string;
  sku: string | null;
}

export interface AdminSkuSyncResult {
  environment: "production" | "sandbox";
  applied: boolean;
  appliedCount: number;
  printfulSyncVariants: number;
  coverage: {
    totalVariations: number;
    matched: number;
    unmatchedCount: number;
  };
  pendingChangeCount: number;
  changes: AdminSkuChange[];
  unmatched: AdminSkuUnmatched[];
  warnings: string[];
}

/** Dry-run: report coverage and the SKU changes that WOULD be applied. */
export const getAdminSkuSync = async (
  authToken: string,
): Promise<ApiResult<AdminSkuSyncResult>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-sku-sync", {
      method: "GET",
      headers: adminAuthHeaders(authToken),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to load SKU sync report: ${error}` };
  }
};

/** Apply: write the corrected SKUs to Square. Requires an explicit confirm. */
export const applyAdminSkuSync = async (
  authToken: string,
): Promise<ApiResult<AdminSkuSyncResult>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-sku-sync", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ apply: true }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to apply SKU sync: ${error}` };
  }
};

// ── Swatch sync (colour bubbles: Square option-value hexes ← Printful blanks) ─

export interface AdminSwatchRow {
  label: string;
  /** Current Square swatch hex (#RRGGBB) or null if unset. */
  current: string | null;
  /** The correct hex derived from the Printful blank (#RRGGBB). */
  correct: string | null;
  pfName: string;
  deltaE: number | null;
  where: string[];
}

export interface AdminSwatchConflict {
  label: string;
  current: string | null;
  options: { hex: string; name: string; count: number; where: string[] }[];
}

/** An option carrying swatch hexes whose Square display isn't "Text and color"
 *  (`CatalogItemOption.showColors`). Report-only — nothing writes that flag. */
export interface AdminSwatchDisplayRow {
  id: string;
  name: string;
  valuesWithHex: number;
  totalValues: number;
  /** False when the option name also fails /colou?r/ — its swatches aren't audited. */
  audited: boolean;
}

export interface AdminSwatchSyncResult {
  environment: "production" | "sandbox";
  /** True when the Printful colour data isn't cached yet — refresh costs first.
   *  When set, the detail fields below are absent and `message` explains. */
  needsRefresh?: boolean;
  message?: string;
  /** When the cached Printful snapshot the colours came from was last built. */
  snapshotUpdatedAt?: string | null;
  applied?: boolean;
  appliedCount?: number;
  threshold?: number;
  colorOptionCount?: number;
  colorValueCount?: number;
  coverage?: { mappedVariations: number; unmappedVariations: number };
  summary?: {
    correct: number;
    wrong: number;
    missing: number;
    conflicting: number;
    noData: number;
    fixable: number;
    displayOff: number;
  };
  wrong?: AdminSwatchRow[];
  missing?: AdminSwatchRow[];
  conflicts?: AdminSwatchConflict[];
  noData?: { label: string; current: string | null; unmapped: number }[];
  displayOff?: AdminSwatchDisplayRow[];
}

/** Dry-run: report which colour swatches are wrong/missing vs the Printful blank. */
export const getAdminSwatchSync = async (
  authToken: string,
): Promise<ApiResult<AdminSwatchSyncResult>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-swatch-sync", {
      method: "GET",
      headers: adminAuthHeaders(authToken),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to load swatch sync report: ${error}` };
  }
};

/** Apply: write the corrected swatch hexes to Square (wrong + missing only). */
export const applyAdminSwatchSync = async (
  authToken: string,
): Promise<ApiResult<AdminSwatchSyncResult>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-swatch-sync", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ apply: true }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to apply swatch sync: ${error}` };
  }
};

// ── Site controls (checkout kill switch + instant catalog cache bust) ─────────

export interface AdminSiteConfig {
  checkoutEnabled: boolean;
  catalogEpoch: number;
  /** Homepage "Classics" carousel — Square ITEM ids. Everything else is "Designs". */
  classicItemIds: string[];
  /** Square ITEM ids in neither homepage carousel (still on sale in the shop). */
  homepageExcludedItemIds: string[];
  updatedAt: string | null;
}

export const getAdminSiteConfig = async (
  authToken: string,
): Promise<ApiResult<AdminSiteConfig>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-site-config", {
      method: "GET",
      headers: adminAuthHeaders(authToken),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to load site config: ${error}` };
  }
};

/** Flip the checkout kill switch. */
export const setAdminCheckoutEnabled = async (
  authToken: string,
  checkoutEnabled: boolean,
): Promise<ApiResult<AdminSiteConfig>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-site-config", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ checkoutEnabled }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to update checkout setting: ${error}` };
  }
};

/**
 * Replace the homepage grouping. Always sends BOTH whole lists (the server
 * replaces rather than merges) so the three choices — Classics, Designs
 * (the remainder) and Neither (excluded) — always land consistent with each
 * other. Two empty arrays mean "no grouping".
 */
export const setAdminHomepageGroups = async (
  authToken: string,
  classicItemIds: string[],
  homepageExcludedItemIds: string[],
): Promise<ApiResult<AdminSiteConfig>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-site-config", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ classicItemIds, homepageExcludedItemIds }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to update homepage groups: ${error}` };
  }
};

/** Bump the catalog epoch → every visitor drops their catalog cache on next load. */
export const bumpAdminCatalogEpoch = async (
  authToken: string,
): Promise<ApiResult<AdminSiteConfig>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-site-config", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ bumpCatalogEpoch: true }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to refresh catalog cache: ${error}` };
  }
};

// ── Open orders (authorized-but-uncaptured, in the free-cancellation window) ──

export interface AdminOpenOrder {
  orderId: string;
  /** Square order state: "OPEN" | "COMPLETED" | "CANCELED" | null. */
  orderState: string | null;
  /** Fulfillment state: "PROPOSED" | "RESERVED" | "COMPLETED" | null. */
  fulfillmentState: string | null;
  /** Captured (charged) but not yet shipped. */
  inFlight: boolean;
  createdAt: string;
  ageMinutes: number;
  withinWindow: boolean;
  minutesUntilCapture: number;
  customerName: string | null;
  email: string | null;
  totalCents: number;
  paymentId: string | null;
  /** "authorized" (free to void) | "captured" | other Square status | "unknown". */
  paymentState: string;
  /** Printful order status: "draft" | "inprocess" | … | null (no draft). */
  printfulStatus: string | null;
  hasPrintfulDraft: boolean;
  /** True once the order is canceled / voided / refunded (Cancelled tab). */
  canceled: boolean;
  /** Amount refunded against the order's payment, in integer cents (0 if none). */
  refundedCents: number;
  /** True only when the payment is still authorized (a free void is possible). */
  cancelable: boolean;
  /** True when a captured, unshipped, not-canceled/refunded order can be refunded. */
  refundable: boolean;
  /** True when Printful has tracking (shipped email can be resent w/ tracking). */
  hasTracking: boolean;
}

export interface AdminOrderDetail extends AdminOpenOrder {
  phone: string | null;
  shippingAddress: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
    country: string | null;
  };
  lineItems: {
    name: string;
    variationName: string | null;
    quantity: number;
    unitCents: number;
    totalCents: number;
  }[];
  subtotalCents: number | null;
  taxCents: number;
  discountCents: number;
  serviceChargeCents: number;
  tracking: {
    carrier: string | null;
    trackingNumber: string;
    trackingUrl: string | null;
  } | null;
}

export interface AdminOpenOrders {
  /** "recent" = last-15-min OPEN; "inflight" = captured-unshipped;
   *  "shipped" = fulfillment COMPLETED; "cancelled" = canceled/refunded;
   *  "search". */
  mode: "recent" | "inflight" | "shipped" | "cancelled" | "search";
  windowMinutes: number;
  /** Time window (days) applied to inflight/shipped/cancelled; 0 = all time. */
  days?: number;
  /** Only present in "recent" mode: the listing window (15 min). */
  recentWindowMinutes?: number;
  /** True when results were capped (search, or a list hit the 200 page cap). */
  truncated?: boolean;
  environment: "production" | "sandbox";
  count: number;
  orders: AdminOpenOrder[];
}

/** List OPEN Square orders from the last 15 minutes (includes
 *  authorized-but-uncaptured ones invisible in the Square dashboard until
 *  finalize-orders captures them). */
export const getAdminOpenOrders = async (
  authToken: string,
): Promise<ApiResult<AdminOpenOrders>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-open-orders", {
      method: "GET",
      headers: adminAuthHeaders(authToken),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to load open orders: ${error}` };
  }
};

/** Look up ALL orders for a buyer by recipient email and/or full name (any
 *  order state). Read-only. */
export const searchAdminOrders = async (
  authToken: string,
  params: { email?: string; name?: string },
): Promise<ApiResult<AdminOpenOrders>> => {
  try {
    const q = new URLSearchParams();
    if (params.email?.trim()) q.set("email", params.email.trim());
    if (params.name?.trim()) q.set("name", params.name.trim());
    const res = await fetch(
      `/.netlify/functions/admin-open-orders?${q.toString()}`,
      {
        method: "GET",
        headers: adminAuthHeaders(authToken),
      },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to search orders: ${error}` };
  }
};

/** List captured-but-unshipped orders (paid, awaiting Printful fulfillment),
 *  created within the last `days` days (0 = all time). */
export const getAdminInFlightOrders = async (
  authToken: string,
  days = 1,
): Promise<ApiResult<AdminOpenOrders>> => {
  try {
    const res = await fetch(
      `/.netlify/functions/admin-open-orders?view=inflight&days=${days}`,
      { method: "GET", headers: adminAuthHeaders(authToken) },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to load in-flight orders: ${error}` };
  }
};

/** List shipped orders (SHIPMENT fulfillment COMPLETED) created within the last
 *  `days` days (0 = all time). These have dropped out of the Square dashboard
 *  "All Orders" view into Shipments → Shipped. */
export const getAdminShippedOrders = async (
  authToken: string,
  days = 1,
): Promise<ApiResult<AdminOpenOrders>> => {
  try {
    const res = await fetch(
      `/.netlify/functions/admin-open-orders?view=shipped&days=${days}`,
      { method: "GET", headers: adminAuthHeaders(authToken) },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to load shipped orders: ${error}` };
  }
};

/** List canceled / voided / refunded orders created within the last `days` days
 *  (0 = all time). Terminal — no void/refund actions. */
export const getAdminCancelledOrders = async (
  authToken: string,
  days = 1,
): Promise<ApiResult<AdminOpenOrders>> => {
  try {
    const res = await fetch(
      `/.netlify/functions/admin-open-orders?view=cancelled&days=${days}`,
      { method: "GET", headers: adminAuthHeaders(authToken) },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to load cancelled orders: ${error}` };
  }
};

/** Full order detail: line items, shipping address, payment, tracking. */
export const getAdminOrderDetail = async (
  authToken: string,
  orderId: string,
): Promise<ApiResult<AdminOrderDetail>> => {
  try {
    const res = await fetch(
      `/.netlify/functions/admin-open-orders?detail=${encodeURIComponent(orderId)}`,
      { method: "GET", headers: adminAuthHeaders(authToken) },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to load order detail: ${error}` };
  }
};

/** Free void of an authorized (uncaptured) order: deletes the Printful draft,
 *  voids the authorized payment (no Square fee), and cancels the Square order. */
export const cancelAdminOpenOrder = async (
  authToken: string,
  orderId: string,
): Promise<ApiResult<{ orderId: string; voided: boolean }>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-open-orders", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ cancel: orderId }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to void order: ${error}` };
  }
};

/** Refund a captured, unshipped order (refund + cancel + email). */
export const refundAdminOrder = async (
  authToken: string,
  orderId: string,
  reason?: string,
): Promise<ApiResult<{ orderId: string; refundedCents: number }>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-open-orders", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ refund: orderId, reason }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to refund order: ${error}` };
  }
};

/** Resend the confirmation ("order received") or shipped email for an order. */
export const resendAdminEmail = async (
  authToken: string,
  orderId: string,
  type: "confirmation" | "shipped",
): Promise<ApiResult<{ orderId: string; resent: string }>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-open-orders", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ resend: orderId, type }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to resend email: ${error}` };
  }
};

// ── Product reviews (moderation queue) ──────────────────────────────────────

export type AdminReviewStatus = "pending" | "published" | "rejected";

export interface AdminReview {
  id: string;
  orderId: string;
  itemId: string;
  itemName: string;
  rating: number;
  text: string;
  author: string;
  createdAt: string;
  status: AdminReviewStatus;
}

export interface AdminReviewsFeed {
  count: number;
  pendingCount: number;
  reviews: AdminReview[];
}

export const getAdminReviews = async (
  authToken: string,
): Promise<ApiResult<AdminReviewsFeed>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-reviews", {
      method: "GET",
      headers: adminAuthHeaders(authToken),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to load reviews: ${error}` };
  }
};

/** Publish, reject, or delete a review by id. */
// ── Return Requests ──────────────────────────────────────────────────────────

export type AdminReturnStatus =
  | "requested"
  | "approved"
  | "received"
  | "denied"
  | "refunded"
  | "canceled";

export interface AdminReturnItem {
  name: string;
  quantity: number;
  /** Suggested refund contribution for this line, in integer cents. */
  cents?: number;
}

export interface AdminReturnRequest {
  id: string;
  originalOrderId: string;
  customerId: string;
  customerEmail: string;
  customerName: string;
  /** One of: defective | wrong_item | not_as_described | changed_mind | other */
  reason: string;
  notes: string;
  items: AdminReturnItem[];
  status: AdminReturnStatus;
  adminNote: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminReturnRequestsFeed {
  count: number;
  pendingCount: number;
  requests: AdminReturnRequest[];
}

export const getAdminReturnRequests = async (
  authToken: string,
): Promise<ApiResult<AdminReturnRequestsFeed>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-returns", {
      method: "GET",
      headers: adminAuthHeaders(authToken),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to load return requests: ${error}` };
  }
};

export const updateAdminReturnStatus = async (
  authToken: string,
  id: string,
  action: "approve" | "deny" | "refund" | "receive" | "cancel",
  adminNote?: string,
  /** Required for the "refund" action: the amount to refund, in integer cents. */
  refundAmount?: number,
): Promise<ApiResult<AdminReturnRequest>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-returns", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ action, id, adminNote, refundAmount }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to update return request: ${error}` };
  }
};

export const moderateAdminReview = async (
  authToken: string,
  id: string,
  action: "publish" | "reject" | "delete",
): Promise<ApiResult<{ id: string; action: string }>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-reviews", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ [action]: id }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to update review: ${error}` };
  }
};

// ── Promo codes + salespeople (CRUD) ────────────────────────────────────────

export interface AdminSalesperson {
  id: string;
  name: string;
  email: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminPromoCode {
  code: string;
  discountType: "percentage" | "fixed";
  /** Present for percentage codes (string like "25.0"); null for fixed. */
  discountPercentage: string | null;
  /** Present for fixed codes (integer cents); null for percentage. */
  discountAmountCents: number | null;
  displayName: string;
  active: boolean;
  /** Inclusive YYYY-MM-DD window; null = no bound. */
  validFrom: string | null;
  validUntil: string | null;
  /** Total uses across ALL customers before the code dies; null = unlimited. */
  maxRedemptions: number | null;
  /** Uses allowed per individual buyer; null = unlimited. Independent of the total. */
  perCustomerLimit: number | null;
  /** Global count backing `maxRedemptions`. Per-customer counts are not summarised here. */
  redemptionCount: number;
  salespersonId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminPromosData {
  codes: AdminPromoCode[];
  salespeople: AdminSalesperson[];
}

/** Editable fields for creating/updating a code (server fills the rest). */
export interface PromoCodeInput {
  code: string;
  discountType: "percentage" | "fixed";
  discountPercentage?: string;
  discountAmountCents?: number;
  displayName: string;
  active: boolean;
  validFrom?: string | null;
  validUntil?: string | null;
  maxRedemptions?: number | null;
  perCustomerLimit?: number | null;
  salespersonId?: string | null;
}

export const getAdminPromos = async (
  authToken: string,
): Promise<ApiResult<AdminPromosData>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-promos", {
      method: "GET",
      headers: adminAuthHeaders(authToken),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to load promo codes: ${error}` };
  }
};

export const savePromoCode = async (
  authToken: string,
  code: PromoCodeInput,
): Promise<ApiResult<AdminPromoCode>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-promos", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ entity: "code", action: "upsert", ...code }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to save promo code: ${error}` };
  }
};

export const deletePromoCode = async (
  authToken: string,
  code: string,
): Promise<ApiResult<{ deleted: string }>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-promos", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ entity: "code", action: "delete", code }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to delete promo code: ${error}` };
  }
};

export const saveSalesperson = async (
  authToken: string,
  person: { id?: string; name: string; email?: string; active?: boolean },
): Promise<ApiResult<AdminSalesperson>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-promos", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({
        entity: "salesperson",
        action: "upsert",
        ...person,
      }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to save salesperson: ${error}` };
  }
};

export const deleteSalesperson = async (
  authToken: string,
  id: string,
): Promise<ApiResult<{ deleted: string }>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-promos", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ entity: "salesperson", action: "delete", id }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to delete salesperson: ${error}` };
  }
};

// ── Promo performance report (collected sales per code / salesperson) ────────

export interface AdminPromoCodeStat {
  code: string;
  displayName: string;
  /** True when the code is on orders but no longer in the roster. */
  retired: boolean;
  salespersonId: string | null;
  salespersonName: string | null;
  orders: number;
  units: number;
  grossRevenueCents: number;
  discountCents: number;
}

export interface AdminPromoSalespersonStat {
  salespersonId: string | null;
  salespersonName: string;
  codes: string[];
  orders: number;
  units: number;
  grossRevenueCents: number;
  discountCents: number;
}

export interface AdminPromoReport {
  environment: "production" | "sandbox";
  days: number;
  generatedAt: string;
  truncated: boolean;
  codes: AdminPromoCodeStat[];
  salespeople: AdminPromoSalespersonStat[];
  totals: {
    orders: number;
    units: number;
    grossRevenueCents: number;
    discountCents: number;
  };
  attributedOrderCount: number;
}

/** Collected sales grouped by promo code + salesperson for the window. Dollars
 *  are recomputed from Square each call. `days` is clamped server-side to a
 *  bounded window (all-time is not allowed — use the quarterly archive). */
export const getAdminPromoReport = async (
  authToken: string,
  days = 30,
): Promise<ApiResult<AdminPromoReport>> => {
  try {
    const res = await fetch(
      `/.netlify/functions/admin-promo-report?days=${days}`,
      { method: "GET", headers: adminAuthHeaders(authToken) },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to load promo report: ${error}` };
  }
};

/** One entry in the quarterly promo-report archive index (newest first). */
export interface AdminPromoArchiveEntry {
  quarter: string; // "YYYY-Qn"
  generatedAt: string;
  totals: AdminPromoReport["totals"] | null;
  attributedOrderCount: number;
}

/** A full archived quarter snapshot (the report is the same shape as live). */
export interface AdminPromoArchiveSnapshot {
  quarter: string;
  label: string;
  generatedAt: string;
  environment: "production" | "sandbox";
  range: { startAt: string; endAt: string };
  report: Omit<AdminPromoReport, "environment" | "days" | "generatedAt">;
}

/** List archived quarters (zero Square calls — served from Blobs). */
export const getPromoArchiveIndex = async (
  authToken: string,
): Promise<ApiResult<{ archives: AdminPromoArchiveEntry[] }>> => {
  try {
    const res = await fetch(
      `/.netlify/functions/admin-promo-report?archived=list`,
      { method: "GET", headers: adminAuthHeaders(authToken) },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to load promo archive: ${error}` };
  }
};

/** Load one archived quarter's snapshot (zero Square calls). */
export const getPromoArchive = async (
  authToken: string,
  quarter: string,
): Promise<ApiResult<AdminPromoArchiveSnapshot>> => {
  try {
    const res = await fetch(
      `/.netlify/functions/admin-promo-report?quarter=${encodeURIComponent(quarter)}`,
      { method: "GET", headers: adminAuthHeaders(authToken) },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to load archived quarter: ${error}` };
  }
};

// ── Needs Attention (durable admin-alert feed) ──────────────────────────────

export interface AdminAlert {
  id: string;
  at: string;
  subject: string;
  message: string;
  context: Record<string, string | number | null> | null;
  dismissed: boolean;
  /** Occurrences collapsed into this entry. A repeat of a still-undismissed
   *  subject bumps this instead of appending. Optional: entries written before
   *  dedup existed have no `count` — treat undefined as 1. */
  count?: number;
  firstSeen?: string;
  lastSeen?: string;
}

export interface AdminAlertsFeed {
  blobsAvailable: boolean;
  count: number;
  activeCount: number;
  alerts: AdminAlert[];
}

export const getAdminAlerts = async (
  authToken: string,
): Promise<ApiResult<AdminAlertsFeed>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-alerts", {
      method: "GET",
      headers: adminAuthHeaders(authToken),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to load alerts: ${error}` };
  }
};

export const dismissAdminAlert = async (
  authToken: string,
  id: string,
): Promise<ApiResult<{ dismissed: string }>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-alerts", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ dismiss: id }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to dismiss alert: ${error}` };
  }
};

export const clearAdminAlerts = async (
  authToken: string,
): Promise<ApiResult<{ cleared: boolean }>> => {
  try {
    const res = await fetch("/.netlify/functions/admin-alerts", {
      method: "POST",
      headers: adminAuthHeaders(authToken),
      body: JSON.stringify({ clear: true }),
    });
    return res.json();
  } catch (error) {
    return { error: `Failed to clear alerts: ${error}` };
  }
};

export type AdminAlertContext = Record<
  string,
  string | number | null | undefined
>;

/**
 * Fire-and-forget admin alert. Sends an email to support@americanaandcompany.com
 * via the send-admin-alert serverless function.
 * Never throws — failures are logged to the console so they never mask the
 * original error being reported.
 */
export const sendAdminAlert = async (
  subject: string,
  message: string,
  context?: AdminAlertContext,
): Promise<ApiResult<{ ok: boolean }>> => {
  try {
    const response = await fetch("/.netlify/functions/send-admin-alert", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
      body: JSON.stringify({ subject, message, context }),
    });
    return response.json();
  } catch (error) {
    console.error("Failed to send admin alert:", error);
    return { error: `Failed to send admin alert: ${error}` };
  }
};

// ── Traffic / acquisition report (where visitors come from) ──────────────────
//
// Two halves with different confidence, deliberately typed as such:
//   • orders/revenue — exact, recomputed from Square each call
//   • visits/conversionRate — approximate; `visits` is 0 and `conversionRate`
//     null when the visit counters were unavailable (check `visitsAvailable`
//     before rendering a zero as fact).

export interface AdminTrafficSourceStat {
  source: string;
  /** Mediums seen for this source, e.g. ["paid_social", "social"]. */
  mediums: string[];
  visits: number;
  orders: number;
  units: number;
  grossRevenueCents: number;
  discountCents: number;
  /** orders/visits, clamped to 1. Null when there's no visit denominator. */
  conversionRate: number | null;
}

export interface AdminTrafficCampaignStat {
  source: string;
  medium: string;
  campaign: string;
  visits: number;
  orders: number;
  units: number;
  grossRevenueCents: number;
  discountCents: number;
  conversionRate: number | null;
}

/** The generator's output (utils/traffic-report.js) — what an archived quarter
 *  stores. The live endpoint wraps this with environment/days/generatedAt; the
 *  archive stores those on the snapshot envelope instead, so the two must not
 *  be conflated. */
export interface AdminTrafficReportData {
  truncated: boolean;
  sources: AdminTrafficSourceStat[];
  campaigns: AdminTrafficCampaignStat[];
  totals: {
    orders: number;
    units: number;
    grossRevenueCents: number;
    discountCents: number;
    visits: number;
    conversionRate: number | null;
  };
  /** Orders carrying a source tag. The rest are grouped as "unattributed". */
  attributedOrderCount: number;
  /** False when Blobs couldn't be read — visit figures are unknown, not zero. */
  visitsAvailable: boolean;
  visitRange: { startDay: string; endDay: string };
}

/** The live report: the generator's output plus the endpoint's own envelope. */
export interface AdminTrafficReport extends AdminTrafficReportData {
  environment: "production" | "sandbox";
  days: number;
  generatedAt: string;
}

/** Visits + collected revenue grouped by acquisition source for the window.
 *  `days` is clamped server-side (all-time is not allowed — use the archive). */
export const getAdminTrafficReport = async (
  authToken: string,
  days = 30,
): Promise<ApiResult<AdminTrafficReport>> => {
  try {
    const res = await fetch(
      `/.netlify/functions/admin-traffic-report?days=${days}`,
      { method: "GET", headers: adminAuthHeaders(authToken) },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to load traffic report: ${error}` };
  }
};

/** One entry in the quarterly traffic-report archive index (newest first). */
export interface AdminTrafficArchiveEntry {
  quarter: string; // "YYYY-Qn"
  generatedAt: string;
  totals: AdminTrafficReport["totals"] | null;
  attributedOrderCount: number;
}

/** A full archived quarter snapshot (the report is the same shape as live). */
export interface AdminTrafficArchiveSnapshot {
  quarter: string;
  label: string;
  generatedAt: string;
  environment: "production" | "sandbox";
  range: { startAt: string; endAt: string };
  report: AdminTrafficReportData;
}

/** List archived quarters (zero Square calls — served from Blobs). */
export const getTrafficArchiveIndex = async (
  authToken: string,
): Promise<ApiResult<{ archives: AdminTrafficArchiveEntry[] }>> => {
  try {
    const res = await fetch(
      `/.netlify/functions/admin-traffic-report?archived=list`,
      { method: "GET", headers: adminAuthHeaders(authToken) },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to load traffic archive: ${error}` };
  }
};

/** Load one archived quarter's snapshot (zero Square calls). */
export const getTrafficArchive = async (
  authToken: string,
  quarter: string,
): Promise<ApiResult<AdminTrafficArchiveSnapshot>> => {
  try {
    const res = await fetch(
      `/.netlify/functions/admin-traffic-report?quarter=${encodeURIComponent(quarter)}`,
      { method: "GET", headers: adminAuthHeaders(authToken) },
    );
    return res.json();
  } catch (error) {
    return { error: `Failed to load archived quarter: ${error}` };
  }
};
